<template>
  <div class="entities">
    <div v-for="(item, index) in entities" :key="index">
        <div class="entities-item" @click="gotoForm(item)">
            <div class="icon" :class="{ 'highlight-icon' : item.form_id === formId }">
            <img src="~@/assets/images/form-icon.png" alt="" />
            </div>
            <div class="content">
              <div class="header" :class="{ 'highlight-header' : item.form_id === formId }">
                <h6>{{ item.template_name }}</h6>
                <span class="date" v-if="item.updated_on">Updated on {{item.updated_on | timeStampToDate('monthDayYear')}}</span>
              </div>
              <div class="details">
                <p class="m mb-2 sequence-value elipsis-text" v-overflow-tooltip v-if="item.sequence_value">{{ item.sequence_value }}</p>
                <p class="updated-by" v-if="item.updated_by">Updated by {{ item.updated_by }}</p>
              </div>
            </div>
        </div>
      <div class="line" v-if="index!==entities.length-1"></div>
    </div>
  </div>
</template>

<script>
import { getTokenStorage } from '@/api/session'
import { getAllFormsForLinking, createLinkingService, deleteLinkingService } from '@/api'
import config from '@/config'
import { timeStampToDate } from '@/filters/dateFilter'
import confirmationDialog from '@/plugins/confirmationDialog'
import { alert, success } from '@/plugins/notification'
export default {
  name: 'LinkedEntities',
  props: {
    entities: {
      type: Array,
      default: () => []
    },
    formId: {
      type: String,
      default: ''
    }
  },
  filters: {
    timeStampToDate
  },
  methods: {
    gotoForm (item) {
      const projectId = getTokenStorage().getItem(config.localstorageKeys.LAST_OPENED_PROJECT)
      const url = `/forms/template/${item.form_template_id}/form/${item.form_id}/view?${config.urlKeys.projectId}=${projectId}&${config.urlKeys.useSessionStorage}=true`
      window.open(url, '_blank', 'noopener,noreferrer')
    },
    gotoFormById (item) {
      const projectId = getTokenStorage().getItem(config.localstorageKeys.LAST_OPENED_PROJECT)
      const templateId = item.template_id
      const url = `/forms/template/${templateId}/form/${item.id}/view?${config.urlKeys.projectId}=${projectId}&${config.urlKeys.useSessionStorage}=true`
      window.open(url, '_blank', 'noopener,noreferrer')
    },
    async openLinkNewFormModal () {
      this.linkNewFormModal = true
      await this.fetchInitialForms()
    },
    closeLinkNewFormModal () {
      this.linkNewFormModal = false
      this.forms = []
      this.filteredForms = []
      this.formSearchText = ''
      this.pageLoading = false
      this.hasMoreForms = true
      this.formsOffset = 0
      this.searchLoading = false
      this.selectedFormIds = []
      this.selectedBatchLinkType = config.LinkTypes.RELATES_TO
      if (this.searchTimer) {
        clearTimeout(this.searchTimer)
        this.searchTimer = null
      }
    },
    async fetchInitialForms () {
      this.forms = []
      this.filteredForms = []
      this.formsOffset = 0
      this.hasMoreForms = true
      this.formSearchText = ''
      this.formsLoading = true
      try {
        await this.loadMoreForms()
      } catch (err) {
        console.log(err)
        this.forms = []
        this.filteredForms = []
      } finally {
        this.formsLoading = false
      }
    },
    buildFormConditions () {
      return {
        _and: [
          { id: { _neq: this.formId } },
          { next_revisions_aggregate: { count: { arguments: ['id'], predicate: { _eq: 0 } } } }
        ]
      }
    },
    mapFormsData (forms = []) {
      return forms
        .map(form => ({
          id: form.id,
          sequence_value: form.sequence_value,
          status: form.status,
          form_type_id: form.form_type_id,
          template_id: form?.template_version?.template_id,
          template_name: form?.template_version?.core_form_template?.name || '--',
          created_on: form.created_on,
          created_by_name: `${form?.created_by_user?.first_name || ''} ${form?.created_by_user?.last_name || ''}`.trim(),
          assigned_to: '--'
        }))
    },
    async loadMoreForms () {
      if (!this.hasMoreForms || this.pageLoading) return
      this.pageLoading = true
      try {
        const response = await getAllFormsForLinking(this.buildFormConditions(), this.pageLimit, this.formsOffset)
        const formsResponse = Array.isArray(response?.core_forms)
          ? response.core_forms
          : (Array.isArray(response?.data?.core_forms) ? response.data.core_forms : [])
        const pageForms = this.mapFormsData(formsResponse)
        this.forms = [...this.forms, ...pageForms]
        this.applySearchFilter()
        this.syncSelectedIds()
        this.formsOffset += this.pageLimit
        this.hasMoreForms = pageForms.length === this.pageLimit
      } catch (err) {
        console.log(err)
      } finally {
        this.pageLoading = false
      }
    },
    handleFormsScroll (event) {
      const target = event?.target
      if (!target || this.pageLoading || !this.hasMoreForms) return
      const threshold = 140
      const distanceFromBottom = target.scrollHeight - target.scrollTop - target.clientHeight
      if (distanceFromBottom <= threshold) {
        this.loadMoreForms()
      }
    },
    applySearchFilter () {
      const search = (this.formSearchText || '').trim().toLowerCase()
      if (!search) {
        this.filteredForms = [...this.forms]
        return
      }
      this.filteredForms = this.forms.filter(item => {
        const formId = (item.sequence_value || '').toLowerCase()
        const templateName = (item.template_name || '').toLowerCase()
        const createdBy = (item.created_by_name || '').toLowerCase()
        return formId.includes(search) || templateName.includes(search) || createdBy.includes(search)
      })
    },
    handleSearch () {
      this.searchLoading = true
      if (this.searchTimer) {
        clearTimeout(this.searchTimer)
      }
      this.searchTimer = setTimeout(() => {
        this.applySearchFilter()
        this.syncSelectedIds()
        this.searchLoading = false
      }, 300)
    },
    setBatchLinkType (linkType) {
      this.selectedBatchLinkType = linkType
    },
    toggleSelectForm (rowId, checked) {
      if (checked) {
        if (!this.selectedFormIds.includes(rowId)) {
          this.selectedFormIds = [...this.selectedFormIds, rowId]
        }
        return
      }
      this.selectedFormIds = this.selectedFormIds.filter(id => id !== rowId)
    },
    toggleSelectAll () {
      if (this.allFilteredSelected) {
        this.selectedFormIds = this.selectedFormIds.filter(id => !this.filteredForms.some(row => row.id === id))
        return
      }
      const filteredIds = this.filteredForms.map(row => row.id)
      this.selectedFormIds = [...new Set([...this.selectedFormIds, ...filteredIds])]
    },
    clearSelection () {
      this.selectedFormIds = []
    },
    syncSelectedIds () {
      const availableIds = new Set(this.forms.map(item => item.id))
      this.selectedFormIds = this.selectedFormIds.filter(id => availableIds.has(id))
    },
    async handleBatchLinkSelected () {
      if (!this.selectedFormIds.length || this.linkingInProgress) return
      this.linkingInProgress = true
      try {
        const selectedForms = this.forms.filter(form => this.selectedFormIds.includes(form.id) && form.id !== this.formId)
        if (!selectedForms.length) {
          alert('No valid forms selected for linking')
          return
        }
        const payload = selectedForms.map(form => ({
          source_feature_id: config.CORE_FEATURES.FORMS,
          source_id: this.formId,
          target_feature_id: config.CORE_FEATURES.FORMS,
          target_id: form.id,
          link_type_id: this.selectedBatchLinkType,
          metadata: JSON.stringify({ form_type_id: form.form_type_id })
        }))
        await createLinkingService(payload)
        success('Selected forms linked successfully')
        this.clearSelection()
        this.$emit('refresh-links')
      } catch (err) {
        console.log(err)
        alert('Failed to link selected forms')
      } finally {
        this.linkingInProgress = false
      }
    },
    handleUnlink (item) {
      confirmationDialog('Are you sure you want to unlink this form?', async (res) => {
        if (!res) return
        try {
          await deleteLinkingService(item.id)
          success('Form unlinked successfully')
          this.$emit('refresh-links')
        } catch (err) {
          console.log(err)
          alert('Failed to unlink form')
        }
      }, 'Unlink', 'Cancel', 'Confirm Unlink')
    },
    async handleLinkTypeClick (row, linkType) {
      if (row.id === this.formId) {
        alert('You cannot link a form to itself')
        return
      }
      if (this.linkingInProgress) return
      this.linkingInProgress = true
      try {
        const payload = {
          source_feature_id: config.CORE_FEATURES.FORMS,
          source_id: this.formId,
          target_feature_id: config.CORE_FEATURES.FORMS,
          target_id: row.id,
          link_type_id: linkType,
          metadata: JSON.stringify({ form_type_id: row.form_type_id })
        }
        await createLinkingService([payload])
        success('Form linked successfully')
        this.selectedFormIds = this.selectedFormIds.filter(id => id !== row.id)
        this.$emit('refresh-links')
      } catch (err) {
        console.log(err)
        alert('Failed to link form')
      } finally {
        this.linkingInProgress = false
      }
    }
  },
  beforeDestroy () {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer)
      this.searchTimer = null
    }
  }
}
</script>

<style lang="scss" scoped>
.entities {
  display: flex;
  flex-direction: column;
  padding: 1rem;
  background-color: var(--bg-color);
  border-radius: 6px;
  height: calc(100vh - 270px);
  overflow-y: auto;

  .line {
      width: 2px;
      background-color: var(--brand-color);
      height: 30px;
      margin-left: 30px;
  }

  .entities-item {
    cursor: pointer;
    display: flex;
    align-items: flex-start;
    background: #f7f9fd;
    border-radius: 12px;
    padding: 1rem;
    position: relative;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);

    &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    }

    .highlight-icon {
      background-color: var(--brand-color) !important;
    }

    .highlight-header {
      color: var(--brand-color);
    }

    .icon {
      width: 40px;
      height: 40px;
      background-color: var(--brand-light-color);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 15px;
      img {
        width: 20px;
        height: 20px;
      }
    }

    .content {
      flex: 1;
      .header {
        display: flex;
        justify-content: space-between;
        align-items: center;

        h6 {
          font-size: 0.9rem;
          margin: 0;
        }

        .date {
          font-size: 0.85rem;
          color: #a0a0a0;
        }
      }

      .details {
        margin-top: 5px;

         .sequence-value {
          max-width: 70%;
         }
        .updated-by {
          font-size: 0.8rem;
          color: #a0a0a0;
          margin-bottom: 5px;
        }

        .route {
          display: flex;
          align-items: center;
          gap: 5px;
          font-weight: 500;
          font-size: 0.9rem;

          .time {
            margin-left: auto;
            font-size: 0.85rem;
            color: #666;
          }
        }

        .pdf-link {
          display: inline-block;
          margin-top: 6px;
          font-size: 0.85rem;
          color: #3d5af1;
          text-decoration: underline;
        }
      }
    }
  }
}
</style>
